// FadeOverlay
// Features:
// - Full-screen fade transition (transparent <-> opaque)
// - Positioned relative to camera to cover view
// - Animated using GSAP

import { useEffect, useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { gsap } from 'gsap'

import { useActions, OVERLAY_STATE } from '../store/useActions'
import { OVERLAY_FADE_DURATION_SEC } from '../utils/constants'

import vertexShader from './shaders/blackoverlay/vertex.glsl'
import fragmentShader from './shaders/blackoverlay/fragment.glsl'

const OVERLAY_EASE = 'power2.inOut'
const PLANE_DISTANCE = 0.5

export default function FadeOverlay() {
    const { camera, size } = useThree()
    const meshRef = useRef()

    const overlayState = useActions((state) => state.overlayState)

    // Geometry
    const geometry = useMemo(() => new THREE.PlaneGeometry(1, 1, 1, 1), [])

    // Material
    const material = useMemo(() => {
        return new THREE.ShaderMaterial({
            uniforms: {
                uOpacity: {
                    value: overlayState === OVERLAY_STATE.opaque ? 1.0 : 0.0,
                },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
            depthTest: false,
            depthWrite: false,
        })
    }, [])

    // Animation: Fade
    useEffect(() => {
        const targetOpacity = overlayState === OVERLAY_STATE.opaque ? 1.0 : 0.0

        gsap.to(material.uniforms.uOpacity, {
            value: targetOpacity,
            duration: OVERLAY_FADE_DURATION_SEC,
            ease: OVERLAY_EASE,
            overwrite: 'auto',
        })
    }, [material, overlayState])

    // Render Loop: Billboard to camera
    useFrame(() => {
        if (!meshRef.current || !camera) return

        // 1. Position in front of camera
        const dir = new THREE.Vector3()
        camera.getWorldDirection(dir)

        meshRef.current.position
            .copy(camera.position)
            .add(dir.multiplyScalar(PLANE_DISTANCE))

        meshRef.current.quaternion.copy(camera.quaternion)

        // 2. Scale to fill frustum
        const height =
            2 * Math.tan((camera.fov * Math.PI) / 360) * PLANE_DISTANCE
        const width = height * (size.width / size.height)

        meshRef.current.scale.set(width, height, 1)
        meshRef.current.renderOrder = 9999
    })

    return <mesh ref={meshRef} geometry={geometry} material={material} />
}
